import { CampaignConfigSchema, CampaignConfigType } from '../types';
import { createDefaultRecurringTemplateSettings } from './recurringTemplateHelpers';
import { createDefaultTextToWinTemplateSettings } from './textToWinTemplateHelpers';
import { createDefaultEnrollmentTemplateSettings } from './enrollmentTemplateHelpers';

/**
 * Pristine config used when creating a new campaign configuration.
 * `updatedAt` is intentionally omitted here — set it at the call site so this
 * value stays a pure, deterministic constant.
 */
export const initialConfig: CampaignConfigSchema = {
  icon: 'Add',
  name: '',
  type: 'custom',
  category: '',
  description: '',
  applicationIds: [],
  message: {
    text: { visible: false, required: true, value: null },
    types: [],
    subject: { visible: false, required: false, value: null },
    data_capture: {
      active: false,
      visible: false,
      dropdown: true,
      required: true,
      description: 'Capture response to message as',
      metadata_name: null,
    },
  },
  messageTemplates: [],
  keywords: {
    values: [],
    visible: true,
    required: false,
    label: 'Keywords',
  },
  recipients: {
    values: [],
    visible: true,
    required: true,
    label: 'Groups',
  },
  messages: {
    max_limit: null,
    min_limit: 1,
  },
  metadata: {
    name: null,
    types: [],
    visible: false,
    required: false,
    description: null,
  },
  opt_out_message: {
    value: 'Reply STOP to opt-out',
    active: true,
    visible: true,
    required: false,
  },
  schedule: {
    value: 'immediately',
    visible: false,
    required: false,
  },
  appointment_type: {
    value: null,
    visible: false,
    required: false,
    label: 'Calendar',
  },
  contact_date_type: {
    value: null,
    visible: false,
    required: false,
    label: 'Contact Date Type',
  },
  attach_calendar_invite: {
    active: false,
    visible: false,
    required: false,
  },
  components: [],
};

/**
 * Compute the config updates to apply when the campaign type changes.
 *
 * Replaces the scattered `switch (type)` blocks from the original
 * ConfigurationSection.handleTypeChange + CampaignConfigBuilder.handleConfigUpdate
 * (opt-out toggle), and adds the type-specific settings blocks for the new
 * `recurring` and `text_to_win` types.
 */
export const applyTypeDefaults = (
  config: CampaignConfigSchema,
  type: CampaignConfigType,
): Partial<CampaignConfigSchema> => {
  const updates: Partial<CampaignConfigSchema> = { type };

  const show = (visible: boolean, required: boolean) => ({ visible, required });

  switch (type) {
    case 'message':
      updates.recipients = { ...config.recipients, ...show(true, true) };
      updates.keywords = { ...config.keywords, ...show(false, false) };
      updates.appointment_type = { ...config.appointment_type, ...show(false, false) };
      updates.contact_date_type = { ...config.contact_date_type, ...show(false, false) };
      break;
    case 'join':
      updates.recipients = { ...config.recipients, ...show(true, true) };
      updates.keywords = { ...config.keywords, ...show(true, true) };
      updates.appointment_type = { ...config.appointment_type, ...show(false, false) };
      updates.contact_date_type = { ...config.contact_date_type, ...show(false, false) };
      break;
    case 'enrollment':
      updates.recipients = { ...config.recipients, ...show(true, true) };
      updates.keywords = { ...config.keywords, ...show(true, true) };
      updates.appointment_type = { ...config.appointment_type, ...show(false, false) };
      updates.contact_date_type = { ...config.contact_date_type, ...show(false, false) };
      updates.enrollmentSettings = config.enrollmentSettings ?? createDefaultEnrollmentTemplateSettings();
      break;
    case 'reminder':
      updates.recipients = { ...config.recipients, ...show(false, false) };
      updates.keywords = { ...config.keywords, ...show(false, false) };
      updates.appointment_type = { ...config.appointment_type, ...show(true, true) };
      updates.contact_date_type = { ...config.contact_date_type, ...show(false, false) };
      break;
    case 'annual':
      updates.recipients = { ...config.recipients, ...show(true, true) };
      updates.keywords = { ...config.keywords, ...show(false, false) };
      updates.appointment_type = { ...config.appointment_type, ...show(false, false) };
      updates.contact_date_type = { ...config.contact_date_type, ...show(true, true) };
      break;
    case 'recurring':
      updates.recipients = { ...config.recipients, ...show(true, true) };
      updates.keywords = { ...config.keywords, ...show(false, false) };
      updates.appointment_type = { ...config.appointment_type, ...show(false, false) };
      updates.contact_date_type = { ...config.contact_date_type, ...show(false, false) };
      updates.recurringSettings = config.recurringSettings ?? createDefaultRecurringTemplateSettings();
      break;
    case 'text_to_win':
      updates.recipients = { ...config.recipients, ...show(false, false) };
      updates.keywords = { ...config.keywords, ...show(true, true) };
      updates.appointment_type = { ...config.appointment_type, ...show(false, false) };
      updates.contact_date_type = { ...config.contact_date_type, ...show(false, false) };
      updates.textToWinSettings = config.textToWinSettings ?? createDefaultTextToWinTemplateSettings();
      break;
    case 'away_message':
      updates.recipients = { ...config.recipients, ...show(false, false) };
      updates.keywords = { ...config.keywords, ...show(false, false) };
      updates.appointment_type = { ...config.appointment_type, ...show(false, false) };
      updates.contact_date_type = { ...config.contact_date_type, ...show(false, false) };
      break;
    default:
      break;
  }

  // Opt-out is active by default for every type except join/enrollment
  // (preserves original handleConfigUpdate behavior).
  updates.opt_out_message = {
    ...config.opt_out_message,
    active: type === 'join' || type === 'enrollment' ? false : true,
  };

  return updates;
};
